import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import {
  ActionIcon,
  Button,
  Card,
  Flex,
  Group,
  Loader,
  Select,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";

import { Can, EmptyMessage } from "@components/shared";
import { PERMISSIONS } from "@util/permissions";

export default function OrganizationAssessmentInstruments({ organizationId }) {
  const { t } = useTranslation();
  const [assigned, setAssigned] = useState([]);
  const [available, setAvailable] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);

  useEffect(() => {
    fetchData();
  }, [organizationId]);

  function fetchData() {
    setLoading(true);
    Promise.all([
      axios.post(
        `/organizations/${organizationId}/retrieve-assessment-instruments/`,
        { page: 0, page_size: 200 }
      ),
      axios.post(`/retrieve-assessment-instruments/`, {
        scope: "active",
        page: 0,
        page_size: 200,
      }),
    ])
      .then(([assignedRes, catalogRes]) => {
        const joined = assignedRes.data.response[0]?.data || [];
        const catalog = catalogRes.data.response[0]?.data || [];
        const catalogById = Object.fromEntries(
          catalog.map((instrument) => [String(instrument.id), instrument])
        );
        setAssigned(
          joined.map((row) => ({
            ...row,
            assessment_instrument:
              row.assessment_instrument ||
              catalogById[String(row.assessment_instrument_id)] ||
              null,
          }))
        );
        const assignedInstrumentIds = new Set(
          joined.map((row) => String(row.assessment_instrument_id))
        );
        setAvailable(
          catalog
            .filter(
              (instrument) =>
                instrument.locked &&
                !assignedInstrumentIds.has(String(instrument.id))
            )
            .map((instrument) => ({
              value: String(instrument.id),
              label: instrument.name,
            }))
        );
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }

  function addInstrument() {
    if (!selectedId) return;
    setBusyKey(`add-${selectedId}`);
    axios
      .post(`/organizations/${organizationId}/assessment-instruments/`, {
        assessment_instrument_id: selectedId,
      })
      .then(() => {
        setSelectedId(null);
        setBusyKey(null);
        toast.success(t("updated"));
        fetchData();
      })
      .catch((err) => {
        setBusyKey(null);
        toast.error(err);
      });
  }

  function removeInstrument(joinId) {
    setBusyKey(`remove-${joinId}`);
    axios
      .delete(
        `/organizations/${organizationId}/assessment-instruments/${joinId}/`
      )
      .then(() => {
        setBusyKey(null);
        toast.success(t("updated"));
        fetchData();
      })
      .catch((err) => {
        setBusyKey(null);
        toast.error(err);
      });
  }

  if (loading) {
    return (
      <Group justify="center" py="xl">
        <Loader size="sm" />
      </Group>
    );
  }

  return (
    <Stack gap="md">
      <Title order={5}>{t("assessment_instrument_plural")}</Title>
      <Can permission={PERMISSIONS.ORG_ASSESSMENT_INSTRUMENTS_MANAGE}>
        <Card>
          <Flex gap="sm" align="flex-end">
            <Select
              style={{ flex: 1 }}
              searchable
              clearable
              placeholder={t("assessment_instrument_singular")}
              data={available}
              value={selectedId}
              onChange={setSelectedId}
            />
            <Button
              leftSection={<IconPlus size={14} />}
              disabled={!selectedId}
              loading={busyKey === `add-${selectedId}`}
              onClick={addInstrument}
            >
              {t("add")}
            </Button>
          </Flex>
        </Card>
      </Can>

      <Stack gap="xs">
        {!assigned.length && <EmptyMessage />}
        {assigned.map((row) => (
          <Card key={row.id}>
            <Flex gap="sm" align="center">
              <Text fw={700} style={{ flexGrow: 1 }}>
                {row.assessment_instrument?.name ||
                  row.assessment_instrument_id}
              </Text>
              <Can permission={PERMISSIONS.ORG_ASSESSMENT_INSTRUMENTS_MANAGE}>
                <ActionIcon
                  size="sm"
                  radius="xl"
                  variant="light"
                  color="red"
                  title={t("remove")}
                  loading={busyKey === `remove-${row.id}`}
                  onClick={() => removeInstrument(row.id)}
                >
                  <IconTrash size={14} />
                </ActionIcon>
              </Can>
            </Flex>
          </Card>
        ))}
      </Stack>
    </Stack>
  );
}
